import { Redirect, useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';

import { SessionErrorScreen } from '@/components/domain/SessionErrorScreen';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Text } from '@/components/ui/Text';
import { useAuth } from '@/lib/auth';
import { redeemTemplateShare } from '@/lib/data';
import { setPendingJoin, takePendingJoin } from '@/lib/pending-join';

import { Centered } from './Centered';

/**
 * Redeem flow for `app/join/template/[token]`. Lives OUTSIDE the `(app)` auth
 * gate so it can stash the token and route through sign-in when the recipient
 * isn't yet authenticated, then resume the redeem afterward. Redeeming grants
 * the caller access to the template (and all its runs) at the link's role.
 */
export function JoinScreen({ token }: { token: string }) {
  const { session, status, retry, signOut } = useAuth();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const redeemed = useRef(false);

  useEffect(() => {
    if (status !== 'ready' || !session || redeemed.current) return;
    redeemed.current = true;
    // We're authenticated and handling the redeem directly now, so clear any
    // stashed token — leaving it set would redirect a later plain sign-in back
    // here. Safe to clear: the auth screens have unmounted by the time we mount.
    takePendingJoin();
    (async () => {
      try {
        const { template_id } = await redeemTemplateShare(token);
        router.replace(`/template/${template_id}`);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [status, session, token, router]);

  // Not authenticated: stash the token so sign-in can resume the redeem. Kept in an
  // effect (not the render body) so rendering stays pure — a thrown/retried render
  // must not mutate the module store.
  useEffect(() => {
    if (status === 'ready' && !session) setPendingJoin({ token });
  }, [status, session, token]);

  // The session couldn't be read, so we can't tell whether to redeem or route to sign-in.
  // Hold on the same recovery screen the (app) group uses rather than spinning forever —
  // including its Sign out, which lands in the `!session` branch below and so still stashes
  // the token and resumes the redeem after re-auth. (The `error` branch further down is a
  // failed REDEEM, which this path never reaches.)
  if (status === 'error') {
    return <SessionErrorScreen onRetry={retry} onSignOut={signOut} />;
  }

  // Allowlist: only a KNOWN session falls through to the sign-in redirect below.
  if (status !== 'ready') {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }

  // Not signed in: the effect above stashed the token; send through sign-in.
  if (!session) {
    return <Redirect href="/sign-in" />;
  }

  if (error) {
    return (
      <Centered>
        <Text variant="title" className="text-center">
          Couldn&rsquo;t open this link
        </Text>
        <Text variant="subtitle" className="text-center">
          {error}
        </Text>
        <Button label="Go home" onPress={() => router.replace('/')} />
      </Centered>
    );
  }

  return (
    <Centered>
      <Spinner />
      <Text variant="caption">Opening shared template…</Text>
    </Centered>
  );
}
